import type {
  SyncWebhookError,
  SyncWebhookErrorResponse,
} from '../../../lib/flotiq-client/types/api-response';

import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../../lib/helpers';

import LogModalCodePreview from '../../../components/WebhookLogs/modal/LogModalCodePreview';

import { WarningIcon } from '../../../images/shapes';

export interface SyncWebhookErrorLogsModalContentType {
  webhookErrors: Record<string, unknown>;
  testId?: string;
}

function isSyncWebhookResponse(obj: unknown): obj is SyncWebhookError {
  if (typeof obj !== 'object' || !obj) return false;
  if (!('__webhook' in obj)) return false;
  const webhook = obj['__webhook'];
  if (typeof webhook !== 'object' || !webhook) return false;
  return 'response' in webhook;
}

export const SyncWebhookErrorLogsModalContent = ({
  webhookErrors,
  testId,
}: SyncWebhookErrorLogsModalContentType) => {
  const { t } = useTranslation();
  if (!isSyncWebhookResponse(webhookErrors)) {
    return (
      <div>
        <span className="text-red-600 dark:text-orange-500 p-2">
          {t('Webhooks.ParsingError')}
        </span>
      </div>
    );
  }
  const response = webhookErrors['__webhook'].response;
  const contentType = response?.contentType?.[0];

  let rawContent = `Unsupported response content-type: ${contentType}`;

  if (contentType === 'application/json') {
    rawContent = JSON.parse(response?.rawContent?.[0] || '{}');
  }

  return (
    <div
      className="p-2 flex flex-col min-h-80 mb-10 md:mb-4"
      {...getTestProps(testId, 'sync-webhook-error-logs-modal-container')}
    >
      <div className="flex gap-2 text-red-600 items-center font-bold mb-6 dark:text-orange-500">
        <WarningIcon className={'w-4 h-4 '} />
        <span className="text-lg">{t('Webhooks.Error')}</span>
      </div>

      <div className="border border-red-600 rounded-md p-3 mb-8 text-lg dark:border-orange-500">
        {(Object.keys(response) as Array<keyof SyncWebhookErrorResponse>).map(
          (key) => {
            if (key === 'rawContent') return null;
            return (
              <div className="m-1 my-2" key={key}>
                <span className="font-bold mr-1 capitalize">{key}:</span>
                {response[key]?.join(' ') || ''}
              </div>
            );
          },
        )}
      </div>

      <span className="font-bold text-lg mb-6">
        {t('Webhooks.ReceivedPayload')}
      </span>
      {rawContent !== undefined && <LogModalCodePreview code={rawContent} />}
    </div>
  );
};
